// ─── Identity of the document row versions THIS tab produced (GH #6303) ─────
//
// `tenant_documents` carries a per-record ROWVERSION, and every write bumps it.
// The editor holds one cached token (`docVersionRef`) and sends it on the next
// save so the server can refuse a write made against a version somebody else
// has already moved past (DB-CONC-001).
//
// The hard question on that path is not "did the row's version change" — it is
// "who changed it". A save produces TWO row versions, not one: the foreground
// write, and the version-snapshot follow-up write that lands afterwards in the
// background (useDocumentState.saveDocument). The cached token has to advance
// over the second one, or the very next save sends a version the server has
// moved past and is refused a conflict nobody caused.
//
// GH #6240 tried to answer "who changed it" by comparing content — adopt a
// live version only when the row looks close enough to what this session last
// sent. That closed the second-tab silent overwrite it was filed for, and
// reopened the mirror bug (GH #6303): between two rapid saves the row and the
// last-sent payload legitimately disagree on real, user-visible fields (title,
// version, versionHistory) even though this tab is the only writer anywhere,
// so the session's own bump read as a stranger's edit and every other save
// self-conflicted.
//
// Content cannot tell the two apart, because both cases look identical in
// content. Identity can. `usePersistence` already draws exactly this
// distinction for the workspace blob — `unverifiedWriteRef`, GH #5191 — by
// remembering what its own write was carrying rather than inferring authorship
// from the row. This module is the document-row equivalent, and it is
// deliberately the narrower, stronger form of it: the tokens THIS tab's own
// writes produced, recorded as they are produced, matched exactly.
//
// Scope is a browser tab. Module state is per JS context, so a second tab —
// `context.newPage()` in the playscripts, a real second window for a user —
// has its own empty registry and can never recognise this tab's versions as
// its own. That is the property #6240 depends on, and it holds by construction
// rather than by comparison.
//
// It is safe across a logout/login in one tab as well: a recorded token only
// ever matches if the server row is STILL on a version this tab wrote, which
// means nothing has been written since, by anyone.
//
// ── The invariant every caller's safety rests on ────────────────────────────
// ONLY a version RETURNED BY ONE OF OUR OWN WRITES may be recorded here. Never
// a version learned from a server response — not a 409's serverDoc, not a
// reload, not a live row from state.documents. The reason is not tidiness: the
// server does not merge a save over the stored row, it replaces it (subject to
// enforceServerOwnedFields), and the client-side merge in
// useDocumentState.saveDocument is what makes that safe. "The row is on a
// version I wrote" is therefore also "the row's content is a subset of my local
// state", which is the whole basis for retrying a refused write without asking
// the user. Record a foreign version and that stops being true: the mechanism
// stops raising a spurious dialog and starts silently overwriting the writer
// who produced it — GH #6240's original bug, re-armed.
//
// Its negative answer is correspondingly weak, and must stay weak: false means
// "not known to be mine", never "proven to be someone else's". Falling out of
// the bounded history below costs a redundant conflict prompt; the alternative
// direction would cost a lost edit.

// Row versions are monotonic per row, so only the most recent handful can ever
// still be the server's current version. Both caps exist to keep a long editing
// session from growing this map without bound; neither is load-bearing for
// correctness beyond that.
const MAX_VERSIONS_PER_DOC = 8;
const MAX_DOCS = 32;

/** @type {Map<string, string[]>} docId → row versions this tab wrote, oldest first */
const ownVersionsByDoc = new Map();

/**
 * Remember that a write issued by THIS tab produced `version` for `docId`.
 * Called from every write path that stamps a fresh row version into local
 * state — the foreground save and its background version-snapshot follow-up
 * alike, since the follow-up is exactly the one the editor cannot otherwise
 * account for.
 */
export function recordOwnDocVersion(docId, version) {
  if (!docId || !version) return;
  const existing = ownVersionsByDoc.get(docId);
  if (existing) {
    if (existing[existing.length - 1] === version) return;
    existing.push(version);
    if (existing.length > MAX_VERSIONS_PER_DOC) existing.shift();
    // Re-insert so map order stays least-recently-written first for the
    // eviction below.
    ownVersionsByDoc.delete(docId);
    ownVersionsByDoc.set(docId, existing);
    return;
  }
  ownVersionsByDoc.set(docId, [version]);
  while (ownVersionsByDoc.size > MAX_DOCS) {
    const oldest = ownVersionsByDoc.keys().next().value;
    if (oldest === undefined) break;
    ownVersionsByDoc.delete(oldest);
  }
}

/**
 * Did one of this tab's own writes produce `version` for `docId`?
 *
 * `true` means the row's current version is this session's own work landing
 * where it was not being watched for — adopt it. `false` means "not known to be
 * mine", never "proven to be someone else's": every caller must keep whatever
 * conservative behaviour it had for that case, because a version this tab
 * genuinely produced can still fall out of the bounded history above.
 */
export function isOwnDocVersion(docId, version) {
  if (!docId || !version) return false;
  const versions = ownVersionsByDoc.get(docId);
  return !!versions && versions.includes(version);
}

/** Test seam only — the registry is process-lifetime state in the browser. */
export function __resetOwnDocVersions() {
  ownVersionsByDoc.clear();
}
